import axios, { isAxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';

export interface BoostOneTimeCodeResponse {
	conversation_id: string;
	one_time_code: string;
}

export interface RequestBoostOneTimeCodeCommand {
	id_token?: string;
	code?: string;
	conversation_id?: string;
	filter_values: string[];
	redirect_uri?: string;
}

export interface IBoostAuthenticationClient {
	/**
	 * Get Boost One Time Code - Get a one time code via Boost AI's authentication service
	 * @param body (RequestBoostOneTimeCodeCommand)
	 * @param cancelToken (optional)
	 * @return OK
	 */
	getBoostOneTimeCode(
		body: RequestBoostOneTimeCodeCommand,
		cancelToken?: CancelToken,
	): Promise<BoostOneTimeCodeResponse>;
}

export class BoostAuthenticationClient implements IBoostAuthenticationClient {
	protected instance: AxiosInstance;
	protected baseUrl: string;
	protected jsonParseReviver: ((key: string, value: unknown) => unknown) | undefined = undefined;

	constructor(instance?: AxiosInstance) {
		this.instance = instance || axios.create();
		this.baseUrl = import.meta.env.VITE_BOOST_AI_DOMAIN || 'https://acorn-insurance.boost.ai';
	}

	getBoostOneTimeCode(
		body: RequestBoostOneTimeCodeCommand,
		cancelToken?: CancelToken,
	): Promise<BoostOneTimeCodeResponse> {
		const url_ = this.baseUrl + '/api/auth/openid/authorize';

		const content_ = body;

		const options_: AxiosRequestConfig = {
			data: content_,
			method: 'POST',
			url: url_,
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			cancelToken,
		};

		return this.instance
			.request(options_)
			.catch((error: unknown) => {
				if (isAxiosError(error) && error.response) {
					return error.response;
				} else {
					throw error;
				}
			})
			.then((response: AxiosResponse) => {
				return this.processOneTimeCode(response);
			});
	}

	protected processOneTimeCode(response: AxiosResponse): Promise<BoostOneTimeCodeResponse> {
		const status = response.status;
		const headers: Record<string, unknown> = {};

		if (response.headers && typeof response.headers === 'object') {
			for (const k in response.headers) {
				if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
					headers[k] = response.headers[k];
				}
			}
		}
		if (status === 200) {
			const responseText = response.data;
			let result200: BoostOneTimeCodeResponse | null = null;
			const resultData200 = responseText as BoostOneTimeCodeResponse;
			result200 = resultData200;
			return Promise.resolve<BoostOneTimeCodeResponse>(result200);
		}
		return Promise.reject(new Error(`Unexpected status code: ${status}`));
	}
}
